import { type MutableRefObject, useCallback } from "react";
import {
  callChangeProxyState,
  callEmailReaderAPI,
  checkElementVisibility,
  compareImages,
  delay,
  executeCurlCommand,
  preRequestErrorMessage,
  getCoordinatesForDevice,
  getErrorMessage,
  getLatestDisplayedUIElements,
  getPropByString,
  invertDirection,
  rememberNetworkValue,
  scrollUntilElementVisible,
  shouldUseAppetizeClone,
  trackExceptionOnFirestore,
  trackSentryException,
  uint8ArrayToBase64,
  wasAudioPlayingInTheLastThreeSeconds,
  withPerfLog,
  canUseKeypress,
  isUppercaseLetter,
  getStepNumberFromGroup,
} from "@/utils/helpers";
import {
  type BuildData,
  type CommandHandlerReturnValue,
  type CompanyData,
  type Dimensions,
  type SettingsData,
} from "@/utils/types";
import axios, { isAxiosError } from "axios";
import type { Subscriber } from "rxjs";
import { type SimulatorSession } from "@/utils/simulator";
import Timeout from "await-timeout";
import type { AppetizeSessionHandle } from "@/utils/appetize";

const useCommandHandler = ({
  appetizeCloneSession,
  appetizeSessionWrapper,
  gptStepsRef,
  audioFramesRef,
  buildData,
  settingsData,
  fromEditor,
  videoFramesRef,
  companyData,
  extractedLinkFromEmail,
}: {
  appetizeSessionWrapper: MutableRefObject<AppetizeSessionHandle>;
  extractedLinkFromEmail: MutableRefObject<string | null>;
  appetizeCloneSession?: MutableRefObject<SimulatorSession | undefined>;
  gptStepsRef: any;
  audioFramesRef: any;
  videoFramesRef: any;
  buildData: BuildData;
  settingsData: SettingsData;
  fromEditor: boolean;
  companyData: CompanyData;
}) => {
  const usingAppetizeClone = shouldUseAppetizeClone({
    settingsData,
    buildData,
    fromEditor,
  });

  const handleExecuteCommand = async (
    callback: () => Promise<any>,
    commandInfo?: string
  ) => {
    try {
      return await callback();
    } catch (e) {
      trackSentryException(e);
      await trackExceptionOnFirestore({ error: e, source: "appetize" });
      throw e;
    }
  };

  const getBase64Screenshot = useCallback(async () => {
    let base64screenshot: string | undefined;

    if (usingAppetizeClone) {
      base64screenshot = await appetizeCloneSession.current?.getScreenshot();
    } else {
      if (videoFramesRef.current.length > 0) {
        base64screenshot = uint8ArrayToBase64(
          videoFramesRef.current.at(-1)?.buffer
        );
      }
    }

    return base64screenshot;
  }, [appetizeCloneSession, usingAppetizeClone, videoFramesRef]);

  const getLatestStepNumber = useCallback(
    () => getStepNumberFromGroup(gptStepsRef.current.at(-1) ?? []),
    [gptStepsRef]
  );

  const handleSetLocation = useCallback(
    async (cmd: string) => {
      const regexMatch = [
        ...cmd.matchAll(
          /lat=([+-]?[0-9]*[.]?[0-9]+).?\s?long=([+-]?[0-9]*[.]?[0-9]+)/g
        ),
      ]?.[0];

      if (regexMatch) {
        const [whole, latitude, longitude] = regexMatch;

        await handleExecuteCommand(
          () =>
            usingAppetizeClone
              ? appetizeCloneSession.current.commands.setLocation({
                  latitude: Number(latitude),
                  longitude: Number(longitude),
                })
              : appetizeSessionWrapper.current
                  .getData()
                  ?.setLocation(Number(latitude), Number(longitude)),
          `setLocation: ${cmd}`
        );
        console.log(`Changing location to ${latitude}, ${longitude}`);
      } else {
        console.log("Failed to extract coordinates from command: ", cmd);
      }
    },
    [usingAppetizeClone, appetizeCloneSession, appetizeSessionWrapper]
  );

  const handleTabOn = useCallback(
    async (
      cmd: string,
      imageDimensions: Dimensions,
      duration?: number
    ): Promise<CommandHandlerReturnValue | undefined> => {
      const durationSecondsMatch = cmd.match(/duration:\s*(\d+(?:\.\d+)?)/i)?.[1];
      if (durationSecondsMatch) {
        duration = Math.round(parseFloat(durationSecondsMatch) * 1000);
      }
      const coordinates = getCoordinatesForDevice(
        usingAppetizeClone
          ? appetizeCloneSession.current.device
          : appetizeSessionWrapper.current?.getData()?.device,
        imageDimensions,
        cmd
      );
      let returnValue = null;

      if (coordinates !== null) {
        const { deviceX, deviceY } = coordinates;
        console.log(`tapping at x=${deviceX} y=${deviceY}`);
        await handleExecuteCommand(
          () =>
            usingAppetizeClone
              ? appetizeCloneSession.current.commands.tap({
                  coordinates: { x: deviceX, y: deviceY },
                  duration: duration ?? 200,
                })
              : appetizeSessionWrapper.current.getData().tap({
                  coordinates: { x: deviceX, y: deviceY },
                  duration: duration ?? 200,
                }),
          `tapOn: ${cmd}`
        );
      } else {
        console.log(`Regex not matched! cmd = [${cmd}]. Tapping on element`);
        let parsedCmd = cmd;
        if (cmd.includes("longPressOn:") || cmd.includes("longPressOn.id:")) {
          parsedCmd = cmd
            .replace("longPressOn:", "tabOn:")
            .replace("longPressOn.id:", "tabOn.id:");
        }
        if (parsedCmd.includes("tabOn.id")) {
          const text = parsedCmd.split("tabOn.id:")[1].trim();
          const match = text.match(
            /["\u201C\u201D]([^"\u201C\u201D]*)["\u201C\u201D]/
          )?.[1];

          returnValue = await handleExecuteCommand(async () => {
            if (usingAppetizeClone) {
              let latestUIElements = await getLatestDisplayedUIElements({
                usingAppetizeClone,
                appetizeCloneSession,
                session: appetizeSessionWrapper,
                settingsData,
              });
              let element = null;
              for (let i = 0; i < 3; i++) {
                if (i !== 0) {
                  await delay(1000);
                  latestUIElements = await getLatestDisplayedUIElements({
                    usingAppetizeClone,
                    appetizeCloneSession,
                    session: appetizeSessionWrapper,
                    settingsData,
                  });
                }
                const check = await checkElementVisibility({
                  elements: latestUIElements,
                  searchConfig: { targetId: match },
                  shouldPreferClickableItems: true,
                });
                element = check.element;
                if (element) {
                  break;
                }
              }

              if (element) {
                console.log("element found, will tap on it: ", element);
                const x = Math.floor(
                  element.bounds.x + element.bounds.width / 2
                );
                const y = Math.floor(
                  element.bounds.y + element.bounds.height / 2
                );
                await appetizeCloneSession.current.commands.tap({
                  coordinates: {
                    x,
                    y,
                  },
                  duration: duration ?? 200,
                });
                return { x, y };
              } else {
                throw new Error(`No clickable element found with id: ${match}`);
              }
            } else {
              const tapPayload = {
                element: {
                  attributes: {
                    ...(appetizeSessionWrapper.current.getData()?.config
                      ?.platform === "android"
                      ? { "resource-id": match }
                      : { accessibilityIdentifier: match }),
                  },
                },
                duration: duration ?? 200,
              };
              try {
                await appetizeSessionWrapper.current.getData()?.tap(tapPayload);
              } catch (e: any) {
                // When multiple elements match, Appetize requires matchIndex to
                // disambiguate. Retry picking the first one. Any other error
                // (e.g. "No element found") propagates as-is.
                if (e?.message?.includes("requires 1 unique element")) {
                  await appetizeSessionWrapper.current
                    .getData()
                    .tap(tapPayload, { matchIndex: 0 });
                } else {
                  throw e;
                }
              }
            }
          }, cmd);
          console.log("Tapped on element with id: ", match, returnValue);
        } else {
          const text = parsedCmd.split("tabOn:")[1].trim();
          const match = text.match(
            /["\u201C\u201D]([^"\u201C\u201D]*)["\u201C\u201D]/
          )?.[1];

          returnValue = await handleExecuteCommand(async () => {
            if (usingAppetizeClone) {
              let latestUIElements = await getLatestDisplayedUIElements({
                usingAppetizeClone,
                appetizeCloneSession,
                session: appetizeSessionWrapper,
                settingsData,
              });
              let element = null;
              for (let i = 0; i < 3; i++) {
                if (i !== 0) {
                  await delay(1000);
                  latestUIElements = await getLatestDisplayedUIElements({
                    usingAppetizeClone,
                    appetizeCloneSession,
                    session: appetizeSessionWrapper,
                    settingsData,
                  });
                }
                const check = await checkElementVisibility({
                  elements: latestUIElements,
                  searchConfig: { text: match },
                  shouldPreferClickableItems: true,
                });
                element = check.element;
                if (element) {
                  break;
                }
              }
              if (element) {
                console.log("element found, will tap on it: ", element);
                const x = Math.floor(
                  element.bounds.x + element.bounds.width / 2
                );
                const y = Math.floor(
                  element.bounds.y + element.bounds.height / 2
                );
                await appetizeCloneSession.current.commands.tap({
                  coordinates: {
                    x,
                    y,
                  },
                  duration: duration ?? 200,
                });
                return { x, y };
              } else {
                throw new Error(
                  `No clickable element found with text: ${match}`
                );
              }
            } else {
              const isAndroid =
                appetizeSessionWrapper!.current.getData()?.config?.platform ===
                "android";
              const shouldUseContentDesc =
                settingsData.useContentDescForLocalAppetizeTap && isAndroid;
              try {
                return await appetizeSessionWrapper.current.getData().tap(
                  {
                    element: {
                      attributes: {
                        [shouldUseContentDesc ? "content-desc" : "text"]: match,
                      },
                    },
                    duration: duration ?? 200,
                  },
                  {
                    matchIndex: 0,
                    timeout: 5000,
                  }
                );
              } catch (e) {
                if (
                  isAndroid &&
                  typeof e?.message === "string" &&
                  e.message.includes("No element found for selector")
                ) {
                  console.log(
                    `No element found for selector, will retry with ${
                      shouldUseContentDesc ? "text" : "content-desc"
                    }`
                  );
                  return appetizeSessionWrapper.current.getData().tap(
                    {
                      element: {
                        attributes: {
                          [shouldUseContentDesc ? "text" : "content-desc"]:
                            match,
                        },
                      },
                      duration: duration ?? 200,
                    },
                    {
                      timeout: 1000,
                    }
                  );
                }
                throw e;
              }
            }
          }, cmd);
          console.log("Tapped on element with text: ", match, returnValue);
        }
        if (returnValue != null) {
          if (returnValue.type === "error") {
            return returnValue;
          } else {
            let xPoint, yPoint;
            try {
              if (usingAppetizeClone) {
                const factor =
                  imageDimensions.width /
                  appetizeCloneSession!.current!.device.screen.width;
                xPoint = Math.floor(returnValue?.x * factor);
                yPoint = Math.floor(returnValue?.y * factor);
              } else {
                const factor =
                  buildData.platform.toLowerCase() === "ios" ? 1.5 : 1;
                const bounds = returnValue?.matchedElements[0]?.bounds;
                xPoint = Math.floor((bounds.x + bounds.width / 2) * factor);
                yPoint = Math.floor((bounds.y + bounds.height / 2) * factor);
              }
              return {
                type: "success",
                data: {
                  appetizeCommands: ` (x=${xPoint}, y=${yPoint})`,
                },
              };
            } catch (e) {
              console.error("Error getting coordinates for tabOn cmd", e);
            }
          }
        }
      }
    },
    [
      usingAppetizeClone,
      appetizeCloneSession,
      appetizeSessionWrapper,
      settingsData,
      buildData.platform,
    ]
  );

  const handleMultiTapOnElement = useCallback(
    async (cmd: string, imageDimensions: Dimensions) => {
      const isById = cmd.startsWith("multiTap.id:");
      const valueMatch = cmd.match(
        /["\u201C\u201D]([^"\u201C\u201D]*)["\u201C\u201D]/
      )?.[1];
      const countMatch = cmd.match(/count:\s*(\d+)/i)?.[1];
      const count = countMatch ? parseInt(countMatch, 10) : 1;

      if (!valueMatch) {
        throw new Error(`Could not parse element from command: ${cmd}`);
      }

      await handleExecuteCommand(async () => {
        if (usingAppetizeClone) {
          let latestUIElements = await getLatestDisplayedUIElements({
            usingAppetizeClone,
            appetizeCloneSession,
            session: appetizeSessionWrapper,
            settingsData,
          });
          let element = null;
          for (let i = 0; i < 3; i++) {
            if (i !== 0) {
              await delay(1000);
              latestUIElements = await getLatestDisplayedUIElements({
                usingAppetizeClone,
                appetizeCloneSession,
                session: appetizeSessionWrapper,
                settingsData,
              });
            }
            const check = await checkElementVisibility({
              elements: latestUIElements,
              searchConfig: isById
                ? { targetId: valueMatch }
                : { text: valueMatch },
              shouldPreferClickableItems: true,
            });
            element = check.element;
            if (element) break;
          }

          if (!element) {
            throw new Error(
              `No clickable element found with ${
                isById ? "id" : "text"
              }: ${valueMatch}`
            );
          }

          const x = Math.floor(element.bounds.x + element.bounds.width / 2);
          const y = Math.floor(element.bounds.y + element.bounds.height / 2);
          await appetizeCloneSession.current.commands.multiTap({
            coordinates: { x, y },
            count,
          });
        } else {
          const isAndroid =
            appetizeSessionWrapper.current.getData()?.config?.platform ===
            "android";
          const tapPayload = isById
            ? {
                element: {
                  attributes: {
                    ...(isAndroid
                      ? { "resource-id": valueMatch }
                      : { accessibilityIdentifier: valueMatch }),
                  },
                },
                duration: 200,
              }
            : {
                element: {
                  attributes: {
                    [settingsData.useContentDescForLocalAppetizeTap && isAndroid
                      ? "content-desc"
                      : "text"]: valueMatch,
                  },
                },
                duration: 200,
              };
          for (let i = 0; i < count; i++) {
            await appetizeSessionWrapper.current
              .getData()
              .tap(tapPayload, { matchIndex: 0 });
          }
        }
      }, cmd);
    },
    [
      usingAppetizeClone,
      appetizeCloneSession,
      appetizeSessionWrapper,
      settingsData,
      handleExecuteCommand,
    ]
  );

  const handleTapInSequence = useCallback(
    async (cmd: string, imageDimensions: Dimensions) => {
      const parts = cmd.split(";");
      const lastPart = parts[parts.length - 1];
      const multiTapCount = /^\d+$/.test(lastPart.trim())
        ? parseInt(lastPart.trim(), 10)
        : 1;

      if (multiTapCount <= 1) {
        await handleTabOn(cmd, imageDimensions);
        return;
      }

      const cmdWithoutCount = parts.slice(0, -1).join(";");
      const coordinates = getCoordinatesForDevice(
        usingAppetizeClone
          ? appetizeCloneSession.current.device
          : appetizeSessionWrapper.current?.getData()?.device,
        imageDimensions,
        cmdWithoutCount
      );

      if (coordinates === null) return;

      const { deviceX, deviceY } = coordinates;
      console.log(
        `multi-tapping ${multiTapCount}x at x=${deviceX} y=${deviceY}`
      );

      if (usingAppetizeClone) {
        await handleExecuteCommand(
          () =>
            appetizeCloneSession.current.commands.multiTap({
              coordinates: { x: deviceX, y: deviceY },
              count: multiTapCount,
            }),
          `multiTap: ${cmdWithoutCount}`
        );
      } else {
        for (let i = 0; i < multiTapCount; i++) {
          await handleExecuteCommand(
            () =>
              appetizeSessionWrapper.current.getData().tap({
                coordinates: { x: deviceX, y: deviceY },
                duration: 200,
              }),
            `tapOn: ${cmdWithoutCount}`
          );
        }
      }
    },
    [
      handleTabOn,
      handleExecuteCommand,
      usingAppetizeClone,
      appetizeCloneSession,
      appetizeSessionWrapper,
    ]
  );

  const handleDoubleTap = useCallback(
    async (cmd: string, imageDimensions: Dimensions) => {
      const isAndroid =
        appetizeSessionWrapper?.current?.data?.config?.platform === "android";

      const coordinates = getCoordinatesForDevice(
        usingAppetizeClone
          ? appetizeCloneSession.current.device
          : appetizeSessionWrapper.current?.getData()?.device,
        imageDimensions,
        cmd
      );

      const tapWithADB = async (deviceX: number, deviceY: number) => {
        const mul = 3;
        const command = `input tap ${mul * deviceX} ${mul * deviceY}`;

        console.log("> Sending ADB command:", command);
        await appetizeSessionWrapper.current.getData().adbShellCommand(command);
        await delay(100);
      };

      if (coordinates !== null) {
        const { deviceX, deviceY } = coordinates;

        console.log(`Double tapping at x=${deviceX} y=${deviceY}`);
        for (let i = 0; i < 3; i++) {
          await handleExecuteCommand(
            () =>
              usingAppetizeClone
                ? appetizeCloneSession.current.commands.tap({
                    coordinates: { x: deviceX, y: deviceY },
                    duration: 200,
                  })
                : isAndroid
                ? tapWithADB(deviceX, deviceY)
                : appetizeSessionWrapper.current.getData().tap({
                    coordinates: { x: deviceX, y: deviceY },
                  }),
            `doubleTap: ${cmd}`
          );
          console.log("tap");
        }
      }
    },
    [fromEditor, usingAppetizeClone]
  );

  const handleZoom = useCallback(
    async (cmd: string) => {
      const regex = /zoom:\s*(in|out)/;
      const match = cmd.match(regex);
      const direction = match ? match[1] : null;

      if (usingAppetizeClone) {
        await handleExecuteCommand(
          () => appetizeCloneSession.current.commands.zoom(direction),
          `zoom: ${cmd}`
        );
      } else {
        throw new Error("Zoom is not supported on this platform");
      }
    },
    [appetizeCloneSession, usingAppetizeClone]
  );

  const handleWait = useCallback(
    async (cmd: string) => {
      const [seconds] = cmd.match(/\d+/g);
      console.log("waiting for ", seconds * 1000, "ms");
      // as there is a risk of appetize closing the session because of inactivity and their heartbeat method not working, we thought about tapping somewhere however this is causing problem with the gigxr build
      // console.log("tapping on 50%:0%");
      // await session.current.getData().tap({ position: { x: "50%", y: "0%" } });
      await handleExecuteCommand(
        () => delay(1000 * seconds),
        `wait: ${seconds}s`
      );
    },
    [appetizeSessionWrapper.current]
  );

  const handleGoTo = useCallback(
    async (cmd: string) => {
      const destination = cmd.split("go to:")[1].trim();

      if (destination.toLowerCase().includes("home")) {
        console.log(`Hitting HOME`);

        await handleExecuteCommand(
          () =>
            usingAppetizeClone
              ? appetizeCloneSession.current.commands.tapHomeScreen()
              : Timeout.wrap(
                  appetizeSessionWrapper.current.getData().keypress("HOME"),
                  10000,
                  "An error occurred while going to home screen"
                ),
          `goTo: ${cmd}`
        );
      }
    },
    [appetizeSessionWrapper.current, usingAppetizeClone]
  );

  const handleAppetizeCloneSwipe = async (
    appetizeCloneSession: SimulatorSession,
    settingsData: SettingsData,
    startCoordinates: any,
    endCoordinates: any
  ) => {
    const customSlideDurations = settingsData.customSlideDurations;
    const platform = (
      appetizeCloneSession.current!.device.platform as string
    ).toLowerCase();
    const slideDuration: number = customSlideDurations[platform] ?? null;

    // Execute the swipe using the new swipe method
    await handleExecuteCommand(
      () =>
        appetizeCloneSession.current.commands.swipe({
          startCoordinates,
          endCoordinates,
          ...(slideDuration !== null ? { duration: slideDuration } : {}),
        }),
      "swipe"
    );
  };

  const handleSlide = useCallback(
    async (cmd: string, imageDimensions: Dimensions) => {
      const regexMatch = [
        ...cmd.matchAll(
          /slide:? (left|right|up|down) (\d+)%:?.*;(\d+);(\d+);?/g
        ),
      ]?.[0];
      if (regexMatch) {
        const direction = regexMatch[1];
        const percentage = parseInt(regexMatch[2]);
        const xPos = parseInt(regexMatch[3]);
        const yPos = parseInt(regexMatch[4]);

        console.log(
          `sliding ${direction} ${percentage}% at x=${xPos} y=${yPos}`
        );

        if (usingAppetizeClone) {
          const startCoordinates = { x: 0, y: 0 };
          const endCoordinates = { x: 0, y: 0 };

          const coordinates = getCoordinatesForDevice(
            appetizeCloneSession.current.device,
            imageDimensions,
            cmd
          );

          const deviceX = coordinates?.deviceX ?? 0;
          const deviceY = coordinates?.deviceY ?? 0;

          const slideLengthY =
            appetizeCloneSession.current.device.screen.height *
            (percentage / 100);
          const slideLengthX =
            appetizeCloneSession.current.device.screen.width *
            (percentage / 100);

          // Calculate swipe direction and movement based on input
          if (["up", "down"].includes(direction)) {
            startCoordinates.x = deviceX;
            endCoordinates.x = deviceX; // Keep X coordinate constant for vertical slides
            if (direction === "up") {
              startCoordinates.y = deviceY;
              endCoordinates.y = deviceY - slideLengthY;
            } else {
              startCoordinates.y = deviceY;
              endCoordinates.y = deviceY + slideLengthY;
            }
          } else {
            startCoordinates.y = deviceY; // Starting Y coordinate
            endCoordinates.y = deviceY; // Keep Y coordinate constant for horizontal slides
            if (direction === "left") {
              startCoordinates.x = deviceX;
              endCoordinates.x = deviceX - slideLengthX;
            } else {
              startCoordinates.x = deviceX;
              endCoordinates.x = deviceX + slideLengthX;
            }
          }
          await handleAppetizeCloneSwipe(
            appetizeCloneSession,
            settingsData,
            startCoordinates,
            endCoordinates
          );
        } else {
          await handleExecuteCommand(
            () =>
              appetizeSessionWrapper.current.getData().swipe({
                gesture: (g) => g[direction](`${percentage}%`),
                position: {
                  x: `${(100 * Number(xPos)) / imageDimensions.width}%`,
                  y: `${(100 * Number(yPos)) / imageDimensions.height}%`,
                },
                duration: 2000, // optional, in ms
              }),
            `slide: ${cmd}`
          );
        }
      } else {
        console.error(`Regex not matched! Command = ${cmd}`);
      }
    },
    [appetizeSessionWrapper.current, usingAppetizeClone, fromEditor]
  );

  const handleType = useCallback(
    async (cmd: string) => {
      const text = cmd.split("type:")[1].trim();
      const surroundedByQuotesRegex = /^"([\s\S]*)"$/;
      let character: string;
      if (text.length > 0 && text.at(0) !== '"') {
        character = text;
      } else if (surroundedByQuotesRegex.test(text)) {
        character = text.match(surroundedByQuotesRegex)?.at(1) ?? "";
      } else {
        character = text.replace(/"/g, "");
      }

      // Removing zero-width characters to prevent errors while typing
      character = character.replace(/[\u200B-\u200D\uFEFF]/g, "");

      console.log(`typing ${character}`);

      if (settingsData.splitTextWhenTyping) {
        for (let i = 0; i < character.length; i++) {
          await handleExecuteCommand(async () => {
            if (usingAppetizeClone) {
              return appetizeCloneSession!.current.commands.type({
                text: character[i],
              });
            } else {
              await delay(800);
              if (
                settingsData.typeUsingKeypress &&
                canUseKeypress(character[i])
              ) {
                const shouldUseShift = isUppercaseLetter(character[i]);
                return await Timeout.wrap(
                  appetizeSessionWrapper!.current
                    .getData()
                    .keypress(character[i], { shift: shouldUseShift }),
                  10000,
                  `An error occurred while tapping the char ${character[i]}`
                );
              } else {
                return appetizeSessionWrapper!.current
                  .getData()
                  .type(character[i]);
              }
            }
          }, `typeChar: ${character[i]}`);
        }
      } else {
        await handleExecuteCommand(
          () =>
            usingAppetizeClone
              ? appetizeCloneSession!.current.commands.type({
                  text: character,
                })
              : appetizeSessionWrapper!.current.getData().type(character),
          `type: ${character}`
        );
      }
    },
    [settingsData, usingAppetizeClone]
  );

  const handleKeypress = useCallback(
    async (cmd: string) => {
      const key = cmd.split("press:")[1].trim();

      if (key.toLowerCase() === "enter") {
        console.log(`Hitting Enter`);
        await handleExecuteCommand(
          () =>
            usingAppetizeClone
              ? appetizeCloneSession.current.commands.keyStroke({
                  keycode: 66,
                })
              : Timeout.wrap(
                  appetizeSessionWrapper.current.getData().keypress("Enter"),
                  10000,
                  "An error occurred while pressing enter"
                ),
          `keypress: ${key}`
        );
      }
    },
    [appetizeSessionWrapper.current, settingsData, usingAppetizeClone]
  );

  const handleSwipeOrScroll = useCallback(
    async (cmd: string) => {
      const regexMatch = [
        ...cmd.matchAll(
          /(swipe|scroll): (right|left|up|down)\s?((\d+) times)?/g
        ),
      ]?.[0];

      if (regexMatch) {
        const swipeOrScroll = regexMatch[1];
        const initialDirection = regexMatch[2];
        const numberOfInteractions = parseInt(regexMatch[4] ?? "1");

        let movementDirection: string = "up";
        movementDirection =
          swipeOrScroll === "scroll"
            ? invertDirection(initialDirection)
            : initialDirection;

        // swipe up at middle of the screen
        const verticalMovement = ["up", "down"].includes(movementDirection);

        for (let i = 0; i < numberOfInteractions; i++) {
          console.log(
            `Performing ${swipeOrScroll} ${initialDirection} ${
              i + 1
            }/${numberOfInteractions}`
          );

          if (usingAppetizeClone) {
            // Coordinates for swiping
            const startCoordinates = { x: 0, y: 0 };
            const endCoordinates = { x: 0, y: 0 };

            // Calculate swipe based on movement direction
            const screenWidth =
              appetizeCloneSession.current.device.screen.width;
            const screenHeight =
              appetizeCloneSession.current.device.screen.height;

            if (verticalMovement) {
              startCoordinates.x = screenWidth / 2;
              endCoordinates.x = screenWidth / 2;
              if (movementDirection === "up") {
                startCoordinates.y = screenHeight * 0.75;
                endCoordinates.y = screenHeight * 0.25;
              } else {
                startCoordinates.y = screenHeight * 0.25;
                endCoordinates.y = screenHeight * 0.75;
              }
            } else {
              startCoordinates.y = screenHeight / 2;
              endCoordinates.y = screenHeight / 2;
              if (movementDirection === "left") {
                startCoordinates.x = screenWidth * 0.9;
                endCoordinates.x = screenWidth * 0.1;
              } else {
                startCoordinates.x = screenWidth * 0.1;
                endCoordinates.x = screenWidth * 0.9;
              }
            }

            await handleAppetizeCloneSwipe(
              appetizeCloneSession,
              settingsData,
              startCoordinates,
              endCoordinates
            );
          } else {
            await handleExecuteCommand(
              () =>
                appetizeSessionWrapper.current.getData().swipe({
                  gesture: (g) =>
                    g[movementDirection](verticalMovement ? "50%" : "100%"),
                  position: {
                    x: verticalMovement
                      ? "50%"
                      : movementDirection === "left"
                      ? "90%"
                      : "10%",
                    y: verticalMovement
                      ? movementDirection === "down"
                        ? "60%"
                        : "40%"
                      : "50%",
                  },
                  duration: 3000, // optional, in ms
                }),
              `${swipeOrScroll}: ${cmd}`
            );
          }
        }
      } else {
        console.log("Regex not matched! cmd = ", cmd);
      }
    },
    [usingAppetizeClone, fromEditor]
  );

  const handleRemoveText = useCallback(
    async (cmd: string) => {
      let keycode: number;
      if (usingAppetizeClone) {
        keycode =
          appetizeCloneSession.current.device.platform === "android" ? 67 : 42;
      }

      let text = cmd.split(":")[1].trim();
      text = text.replace(/"/g, "");
      console.log("removing " + text);

      for (let i = 0; i < text.length; i++) {
        await delay(100 * i);
        console.log("Hitting backspace");
        await handleExecuteCommand(
          () =>
            usingAppetizeClone
              ? appetizeCloneSession.current.commands.keyStroke({
                  keycode,
                })
              : Timeout.wrap(
                  appetizeSessionWrapper.current
                    .getData()
                    .keypress("Backspace"),
                  10000,
                  "An error occurred while removing text"
                ),
          `keypress: Backspace`
        );
      }
    },
    [appetizeCloneSession, appetizeSessionWrapper, usingAppetizeClone]
  );

  const handleOpenDeepLink = useCallback(
    async (cmd: string) => {
      let link =
        cmd.split("deeplink:")[1]?.trim() ?? cmd.split("openLink:")[1]?.trim();
      link = link.replace(/"/g, ""); // Remove all double quotes
      if (link === "{{extractedLink}}") {
        link = extractedLinkFromEmail.current;
      }
      console.log("Opening link ", link);

      await handleExecuteCommand(
        () =>
          usingAppetizeClone
            ? appetizeCloneSession.current.commands.openDeepLinkUrl(link)
            : appetizeSessionWrapper.current.getData().openUrl(link),
        cmd.startsWith("openLink:") ? `openLink: ${link}` : `deeplink: ${link}`
      );
    },
    [
      extractedLinkFromEmail,
      usingAppetizeClone,
      appetizeCloneSession,
      appetizeSessionWrapper,
    ]
  );

  const handleOpenAppClipLink = useCallback(
    async (cmd: string) => {
      let link = cmd.split("openAppClipLink:")[1]?.trim();
      link = link.replace(/"/g, "");
      console.log("Opening app clip link ", link);

      await handleExecuteCommand(
        () => appetizeCloneSession.current.commands.openAppClipLink(link),
        `openAppClipLink: ${link}`
      );
    },
    [appetizeCloneSession]
  );

  const handleShakeDevice = useCallback(
    async () =>
      await handleExecuteCommand(async () => {
        if (usingAppetizeClone) {
          appetizeCloneSession.current.commands.shakeDevice();
        } else {
          if (
            appetizeSessionWrapper.current.getData()?.config?.platform ===
            "android"
          ) {
            const command = "input keyevent 82";
            console.log("Shaking the device with command: ", command);
            await appetizeSessionWrapper.current
              .getData()
              .adbShellCommand(command);
          } else {
            await appetizeSessionWrapper.current.getData().shake();
          }
        }
      }, "shakeDevice"),
    [
      appetizeSessionWrapper.current,
      appetizeCloneSession.current,
      usingAppetizeClone,
      buildData,
    ]
  );

  const handleBackAndroid = useCallback(
    async () =>
      await handleExecuteCommand(async () => {
        if (usingAppetizeClone) {
          await appetizeCloneSession.current?.commands.keyStroke({
            keycode: 4,
          });
        } else {
          if (
            appetizeSessionWrapper.current?.getData()?.config?.platform ===
            "android"
          ) {
            const command = "input keyevent 4";
            console.log("Going back with command: ", command);
            await appetizeSessionWrapper.current
              .getData()
              .adbShellCommand(command);
          } else {
            throw new Error("Back button is not supported on this platform");
          }
        }
      }, "back"),
    [appetizeCloneSession, appetizeSessionWrapper, usingAppetizeClone]
  );

  const handleRestartApp = useCallback(
    async () =>
      await handleExecuteCommand(async () => {
        if (usingAppetizeClone) {
          console.log(">> Restarting the app");
          appetizeCloneSession.current.commands.restartApp();
        } else {
          console.log(">> Restarting the app");
          await appetizeSessionWrapper.current.getData().restartApp();
        }
      }, "restartApp"),
    [
      appetizeSessionWrapper.current,
      appetizeCloneSession.current,
      usingAppetizeClone,
    ]
  );

  const handleTerminateApp = useCallback(
    async () =>
      await handleExecuteCommand(async () => {
        if (usingAppetizeClone) {
          console.log(">> Terminating the app");
          await appetizeCloneSession.current.commands.terminateApp();
        } else {
          throw new Error(
            "terminateApp is only supported on our own infrastructure"
          );
        }
      }, "terminateApp"),
    [appetizeCloneSession, usingAppetizeClone]
  );

  const handleExecute = useCallback(
    async (cmd: string) => {
      const regexMatch = [
        ...cmd.matchAll(/.*(GET|DELETE).*url=(.*);json selector=(.*)/g),
      ]?.[0];
      const jsonlessRegexMatch = [
        ...cmd.matchAll(/.*(GET|DELETE).*url=(.*)/g),
      ]?.[0];
      let [whole, method, url, json_selector] = ["", "", "", ""];
      if (regexMatch) {
        [whole, method, url, json_selector] = regexMatch;
      } else if (jsonlessRegexMatch) {
        [whole, method, url] = jsonlessRegexMatch;
      } else {
        throw new Error(`Incorrect command passed: ${cmd}`);
      }

      console.log(
        `Executing ${method} network call to ${url} with json selector = ${json_selector}`
      );

      await handleExecuteCommand(async () => {
        try {
          const response_json = await executeCurlCommand(
            `curl -X ${method} ${url}`,
            buildData?.organisationId
          );

          if (isAxiosError(response_json)) {
            const msg = getErrorMessage(response_json);
            throw new Error(`Network request failed with an error - ${msg}`);
          }

          console.log("Response ", response_json);
          const data = getPropByString(response_json.data, json_selector);
          console.log("Data", data);
          rememberNetworkValue(gptStepsRef, JSON.stringify(data));
        } catch (e) {
          trackSentryException(e);
          if (e instanceof SyntaxError) {
            console.error(
              "Error occured while handling json selector for network request",
              e
            );
          } else {
            throw e;
          }
        }
      }, `execute: ${method} ${url}`);
    },
    [appetizeSessionWrapper.current, buildData]
  );

  const handleVerifyEmail = useCallback(
    async (cmd: string) => {
      const email = cmd.match(/email=(.*);link_pattern=(.*)/)?.[1];
      const link_pattern = cmd.match(/email=(.*);link_pattern=(.*)/)?.[2];
      if (email === undefined || link_pattern === undefined) {
        throw new Error(
          `Incorrect data passed: email=${email}, json link_pattern=${link_pattern}`
        );
      }
      console.log(
        `Veryfying email ${email} with link_pattern = ${link_pattern}`
      );
      await handleExecuteCommand(async () => {
        let link;
        for (let i = 0; i < 2; i++) {
          try {
            link = await callEmailReaderAPI(email!, link_pattern!);
            if (link) {
              gptStepsRef.current[gptStepsRef.current.length - 1] = [
                ...gptStepsRef.current.at(-1),
                `remember: "email data value":"${JSON.stringify(link)}"`,
              ];
              break;
            }
          } catch (e) {
            trackSentryException(e);
            console.log("Error occurred during execution of network call", e);
            console.log("Waiting 10 seconds and retrying...");
            await new Promise((r) => setTimeout(r, 1000 * 10));
          }
        }
        if (!link) {
          throw new Error(`Incorrect link = ${link}`);
        }
      }, `verifyEmail: ${email}`);
    },
    [appetizeSessionWrapper.current]
  );

  const handleExtractLinkFromEmail = useCallback(
    async (cmd: string) => {
      const pattern = /extractLinkFromEmail:\s*([^;]+);(.+)/;
      const match = cmd.match(pattern);

      if (match) {
        const data = {
          email: match[1].trim(),
          url: match[2].trim(),
        };
        await handleExecuteCommand(async () => {
          let link;
          for (let i = 0; i < 2; i++) {
            try {
              link = await callEmailReaderAPI(data.email, data.url);
              if (link) {
                extractedLinkFromEmail.current = link;
                break;
              }
            } catch (e) {
              trackSentryException(e);
              console.log("Error occurred during execution of network call", e);
              console.log("Waiting 10 seconds and retrying...");
              await new Promise((r) => setTimeout(r, 1000 * 10));
            }
          }
          if (!link) {
            throw new Error(`Incorrect link = ${link}`);
          }
        }, `extractLinkFromEmail: ${data.email}`);
      } else {
        throw new Error(`Incorrect command passed: ${cmd}`);
      }
    },
    [extractedLinkFromEmail]
  );

  const handleCheckAudio = useCallback(async () => {
    let audioWasPlaying = false;

    if (usingAppetizeClone) {
      try {
        audioWasPlaying =
          (await appetizeCloneSession?.current?.commands.checkAudio()) === true;
      } catch (e) {
        trackSentryException(e);
        console.log("Error checking audio on device gateway", e);
      }
    } else {
      audioWasPlaying = wasAudioPlayingInTheLastThreeSeconds(
        audioFramesRef.current
      );
    }

    if (audioWasPlaying) {
      gptStepsRef.current[gptStepsRef.current.length - 1] = [
        ...gptStepsRef.current[gptStepsRef.current.length - 1],
        `remember: "audio was being played"`,
      ];
    } else {
      gptStepsRef.current[gptStepsRef.current.length - 1] = [
        ...gptStepsRef.current[gptStepsRef.current.length - 1],
        `remember: "audio was not being played"`,
      ];
    }
  }, [appetizeSessionWrapper.current, usingAppetizeClone, appetizeCloneSession]);

  const handleLaunchApp = useCallback(
    async (cmd: string) => {
      const appId = cmd.split("launchApp:")[1].trim().replace(/"/g, "");
      console.log(`>> Launching app ${appId}`);
      await handleExecuteCommand(
        () => appetizeSessionWrapper.current.getData().launchApp(appId),
        `launchApp: ${appId}`
      );
    },
    [appetizeSessionWrapper.current]
  );

  const handleBiometry = useCallback(
    async (cmd: string) => {
      const match: boolean =
        cmd
          .split("matchBiometry:")[1]
          .trim()
          .replace(/"/g, "")
          .toLowerCase() === "true";
      console.log(`>> Sending biometry with match = ${match}`);
      await handleExecuteCommand(
        () =>
          usingAppetizeClone
            ? appetizeCloneSession.current.commands.biometry(match)
            : appetizeSessionWrapper.current.getData().biometry({ match }),
        `biometry: ${match}`
      );
    },
    [appetizeSessionWrapper.current, usingAppetizeClone]
  );

  const handleGoingOfflineAndOnline = useCallback(
    async (cmd: string): Promise<CommandHandlerReturnValue | undefined> => {
      const shouldStart = cmd.toLowerCase().includes("false");
      const label = `offlineMode: ${shouldStart ? "disable" : "enable"}`;
      return await handleExecuteCommand(async () => {
        try {
          console.log(">> Handling offline mode command");
          const methodEnabled = settingsData.enableOfflineModeCommand;
          const isAndroid = buildData.platform.toLowerCase() === "android";
          if (methodEnabled) {
            if (usingAppetizeClone) {
              const sessionId = appetizeCloneSession?.current?.info?.session_id;
              const serverUrl = appetizeCloneSession?.current?.serverUrl;
              const action = shouldStart ? "disable" : "enable";

              if (sessionId && serverUrl) {
                await axios.post(
                  `${serverUrl}/interaction/offline_mode/${action}/${sessionId}`
                );
              } else {
                throw new Error("Session ID or Server URL not found");
              }
            } else if (isAndroid) {
              const innerCommand = shouldStart ? "enable" : "disable";
              await appetizeSessionWrapper.current
                .getData()
                .adbShellCommand(`svc wifi ${innerCommand}`);
              await appetizeSessionWrapper.current
                .getData()
                .adbShellCommand(`svc data ${innerCommand}`);
            } else {
              await callChangeProxyState(settingsData.proxy, shouldStart);
            }
          } else {
            console.error(">> Going offline is not supported");
            throw new Error("Offline mode is not supported for this account");
          }
        } catch (e) {
          return {
            type: "error",
            error: getErrorMessage(e),
          };
        }
      }, label);
    },
    [
      buildData.platform,
      appetizeSessionWrapper,
      settingsData.enableOfflineModeCommand,
      settingsData.proxy,
      usingAppetizeClone,
    ]
  );

  const handleCurlRequest = useCallback(
    async (cmd: string) =>
      await handleExecuteCommand(async () => {
        const command = cmd.split("curlRequest:")[1].trim();
        const curlResponse = await executeCurlCommand(
          command,
          buildData?.organisationId
        );

        if (isAxiosError(curlResponse)) {
          // preRequestErrorMessage, not getErrorMessage: an AxiosError's own
          // message is only ever "Request failed with status code N", which
          // says nothing about which call failed or what it was for.
          const msg = preRequestErrorMessage(curlResponse);
          return {
            type: "error",
            error: `Curl request failed with an error - ${msg}`,
          };
        } else {
          const valueToRemember = JSON.stringify(curlResponse.data);
          rememberNetworkValue(gptStepsRef, valueToRemember);
        }
      }, "curlRequest"),
    [buildData?.organisationId, gptStepsRef]
  );

  const handleAssertVisible = useCallback(
    async (cmd: string, uiElements: any[]) => {
      return await handleExecuteCommand(async () => {
        if (cmd.includes("assertVisible.id:")) {
          const text = cmd.split("assertVisible.id:")[1].trim();
          const match = text.match(
            /["\u201C\u201D]([^"\u201C\u201D]+)["\u201C\u201D]\s*(.*)\s*Result=true/
          );
          const id = match?.[1];
          const hintForModel = match?.[2];
          if (id === undefined) {
            throw new Error(`Incorrect command passed: ${cmd}`);
          }

          let latestUIElements = uiElements;
          let elementExists = false;
          let lastScreenshot;
          for (let i = 0; i < 3; i++) {
            if (i !== 0) {
              await delay(1000);
              latestUIElements = await getLatestDisplayedUIElements({
                usingAppetizeClone,
                appetizeCloneSession,
                session: appetizeSessionWrapper,
                settingsData,
              });
            }
            lastScreenshot = await getBase64Screenshot();
            const check = await checkElementVisibility({
              elements: latestUIElements,
              searchConfig: { targetId: id },
              screenshot: lastScreenshot,
              isAssertVisible: true,
            });
            elementExists = check.found;
            if (elementExists) {
              break;
            }
          }

          if (!elementExists) {
            if (
              settingsData.enableCommandsAIFallback &&
              settingsData.enableAssertVisibleAIFallback
            ) {
              if (hintForModel !== undefined && hintForModel.length > 0) {
                throw new Error(
                  `UI elements check failed: Asserting ${hintForModel} is visible`
                );
              } else {
                throw new Error(`Element with id ${id} not found`);
              }
            } else {
              console.log("will fail inside command handler", {
                type: "error",
                error: `Element with id ${id} not found`,
                data: {
                  shouldReplaceLastInteractionLog: true,
                  screenshot: lastScreenshot,
                },
              });
              const lastStepNumber = getLatestStepNumber();
              let errorMessage = `Element with id ${id} not found`;
              if (lastStepNumber) {
                errorMessage =
                  `[Step ${lastStepNumber}] assertVisible.id: ` + errorMessage;
              }

              return {
                type: "error",
                error: errorMessage,
                data: {
                  shouldReplaceLastInteractionLog: true,
                  screenshot: lastScreenshot,
                },
              };
            }
          }
        } else if (cmd.includes("assertVisible.text:")) {
          const text = cmd.split("assertVisible.text:")[1].trim();
          const match = text.match(
            /["\u201C\u201D]([^"\u201C\u201D]+)["\u201C\u201D]\s*(.*)\s*Result=true/
          );
          const targetText = match?.[1];
          const hintForModel = match?.[2];

          if (targetText === undefined) {
            throw new Error(`Incorrect command passed: ${cmd}`);
          }

          let latestUIElements = uiElements;
          let textFound = false;
          let lastScreenshot;
          for (let i = 0; i < 3; i++) {
            if (i !== 0) {
              await delay(1000);
              latestUIElements = await getLatestDisplayedUIElements({
                usingAppetizeClone,
                appetizeCloneSession,
                session: appetizeSessionWrapper,
                settingsData,
              });
            }
            lastScreenshot = await getBase64Screenshot();
            const check = await checkElementVisibility({
              elements: latestUIElements,
              searchConfig: { text: targetText },
              screenshot: lastScreenshot,
              isAssertVisible: true,
            });
            textFound = check.found;
            if (textFound) {
              break;
            }
          }

          if (!textFound) {
            if (
              settingsData.enableCommandsAIFallback &&
              settingsData.enableAssertVisibleAIFallback
            ) {
              if (hintForModel !== undefined && hintForModel.length > 0) {
                throw new Error(
                  `UI elements check failed: Asserting ${hintForModel} is visible`
                );
              } else {
                throw new Error(`Element with text "${targetText}" not found`);
              }
            } else {
              const lastStepNumber = getLatestStepNumber();
              let errorMessage = `Element with text ${targetText} not found`;
              if (lastStepNumber) {
                errorMessage =
                  `[Step ${lastStepNumber}] assertVisible.text: ` +
                  errorMessage;
              }

              return {
                type: "error",
                error: errorMessage,
                data: {
                  shouldReplaceLastInteractionLog: true,
                  screenshot: lastScreenshot,
                },
              };
            }
          }
        }
        // If neither assertVisible.id nor assertVisible.text is specified
        else {
          throw new Error(`Unrecognized assertVisible command: ${cmd}`);
        }
      }, cmd);
    },
    [
      getBase64Screenshot,
      usingAppetizeClone,
      appetizeCloneSession,
      appetizeSessionWrapper,
      getLatestStepNumber,
      settingsData,
    ]
  );
  const handleAssertNotVisible = useCallback(
    async (cmd: string, uiElements: any[]) => {
      if (cmd.includes("assertNotVisible.id:")) {
        const text = cmd.split("assertNotVisible.id:")[1].trim();

        const match = text.match(
          /["\u201C\u201D]([^"\u201C\u201D]+)["\u201C\u201D]\s*(.*)\s*Result=true/
        );
        const id = match?.[1];
        const hintForModel = match?.[2];

        if (id === undefined) {
          throw new Error(`Incorrect command passed: ${cmd}`);
        }

        let latestUIElements = uiElements;
        let elementExists = false;
        let lastScreenshot;
        for (let i = 0; i < 3; i++) {
          if (i !== 0) {
            await delay(1000);
            latestUIElements = await getLatestDisplayedUIElements({
              usingAppetizeClone,
              appetizeCloneSession,
              session: appetizeSessionWrapper,
              settingsData,
            });
          }
          lastScreenshot = await getBase64Screenshot();
          const check = await checkElementVisibility({
            elements: latestUIElements,
            searchConfig: { targetId: id },
            screenshot: lastScreenshot,
            isAssertVisible: true,
          });
          elementExists = check.found;
          if (elementExists) {
            break;
          }
        }

        if (elementExists) {
          if (
            settingsData.enableCommandsAIFallback &&
            settingsData.enableAssertVisibleAIFallback
          ) {
            if (hintForModel !== undefined && hintForModel.length > 0) {
              throw new Error(
                `UI elements check failed: Asserting ${hintForModel} is not visible`
              );
            } else {
              throw new Error(
                `Element with id ${id} should not be visible but was found`
              );
            }
          } else {
            const lastStepNumber = getLatestStepNumber();
            let errorMessage = `Element with id ${id} should not be visible but was found`;
            if (lastStepNumber) {
              errorMessage =
                `[Step ${lastStepNumber}] assertNotVisible.id: ` + errorMessage;
            }
            return {
              type: "error",
              error: errorMessage,
              data: {
                shouldReplaceLastInteractionLog: true,
                screenshot: lastScreenshot,
              },
            };
          }
        }
      } else if (cmd.includes("assertNotVisible.text:")) {
        const text = cmd.split("assertNotVisible.text:")[1].trim();

        const match = text.match(
          /["\u201C\u201D]([^"\u201C\u201D]+)["\u201C\u201D]\s*(.*)\s*Result=true/
        );
        const targetText = match?.[1];
        const hintForModel = match?.[2];

        if (targetText === undefined) {
          throw new Error(`Incorrect command passed: ${cmd}`);
        }

        let latestUIElements = uiElements;
        let textFound = false;
        let lastScreenshot;
        for (let i = 0; i < 3; i++) {
          if (i !== 0) {
            await delay(1000);
            latestUIElements = await getLatestDisplayedUIElements({
              usingAppetizeClone,
              appetizeCloneSession,
              session: appetizeSessionWrapper,
              settingsData,
            });
          }
          lastScreenshot = await getBase64Screenshot();
          const check = await checkElementVisibility({
            elements: latestUIElements,
            searchConfig: { text: targetText },
            screenshot: lastScreenshot,
            isAssertVisible: true,
          });
          textFound = check.found;
          if (textFound) {
            break;
          }
        }

        if (textFound) {
          if (
            settingsData.enableCommandsAIFallback &&
            settingsData.enableAssertVisibleAIFallback
          ) {
            if (hintForModel !== undefined && hintForModel.length > 0) {
              throw new Error(
                `UI elements check failed: Asserting ${hintForModel} is not visible`
              );
            } else {
              throw new Error(
                `Element with text "${targetText}" should not be visible but was found`
              );
            }
          } else {
            const lastStepNumber = getLatestStepNumber();
            let errorMessage = `Element with text ${targetText} should not be visible but was found`;
            if (lastStepNumber) {
              errorMessage =
                `[Step ${lastStepNumber}] assertNotVisible.text: ` +
                errorMessage;
            }

            return {
              type: "error",
              error: errorMessage,
              data: {
                shouldReplaceLastInteractionLog: true,
                screenshot: lastScreenshot,
              },
            };
          }
        }
      }
      // If neither assertNotVisible.id nor assertNotVisible.text is specified
      else {
        throw new Error(`Unrecognized assertNotVisible command: ${cmd}`);
      }
    },
    [
      appetizeCloneSession,
      getBase64Screenshot,
      getLatestStepNumber,
      appetizeSessionWrapper,
      settingsData,
      usingAppetizeClone,
    ]
  );

  const handleScrollUntilIdVisible = useCallback(
    async (cmd: string, uiElements: any[], observer: Subscriber<unknown>) => {
      if (
        !usingAppetizeClone &&
        buildData.platform.toLowerCase() !== "android"
      ) {
        throw new Error(
          "handleScrollUntilVisible.id is not supported on this platform"
        );
      }

      const text = cmd.split(/scroll(?:Up|Down)UntilVisible\.id:/)[1].trim();
      const isUp = cmd.includes("scrollUpUntilVisible");
      const match = text.match(
        /["\u201C\u201D]([^"\u201C\u201D]+)["\u201C\u201D]\s*(.*)\s*Result=true/
      );
      const searchValue = match?.[1];
      const hintForModel = match?.[2];

      if (searchValue === undefined) {
        throw new Error(`Incorrect command passed: ${cmd}`);
      }

      await scrollUntilElementVisible({
        searchConfig: { targetId: searchValue },
        searchValue,
        isUp,
        hintForModel,
        observer,
        usingAppetizeClone,
        appetizeCloneSession,
        session: appetizeSessionWrapper,
        handleSwipeOrScroll,
        getBase64Screenshot,
        settingsData,
      });
    },
    [
      appetizeCloneSession,
      buildData.platform,
      getBase64Screenshot,
      handleSwipeOrScroll,
      appetizeSessionWrapper,
      usingAppetizeClone,
      settingsData,
    ]
  );

  const handleScrollToBottom = useCallback(
    async (cmd: string, observer: Subscriber<unknown>) => {
      console.log("Scrolling to bottom");

      let prevScreenshot = await getBase64Screenshot();
      if (prevScreenshot === null) {
        return;
      }

      console.log("prevScreenshot", prevScreenshot);

      while (true) {
        if (observer.closed) {
          break;
        }
        await handleSwipeOrScroll(`scroll: down`);
        console.log("scrolled down");

        const currentScreenshot = await getBase64Screenshot();
        console.log("currentScreenshot", currentScreenshot);
        console.log("prevScreenshot", prevScreenshot);
        console.log("currentScreenshot", currentScreenshot);

        const compareResult = await compareImages(
          prevScreenshot,
          currentScreenshot
        );
        console.log("compareResult", compareResult);
        const screenshotsEqual = compareResult === 0;
        if (screenshotsEqual) {
          console.log("Screenshots are equal, breaking the loop");
          break;
        }
        prevScreenshot = currentScreenshot;
      }
    },
    [appetizeSessionWrapper.current, usingAppetizeClone]
  );

  const handleScrollUntilTextVisible = useCallback(
    async (cmd: string, uiElements: any[], observer: Subscriber<unknown>) => {
      const text = cmd.split(/scroll(?:Up|Down)UntilVisible\.text:/)[1].trim();
      const isUp = cmd.includes("scrollUpUntilVisible");
      const match = text.match(
        /["\u201C\u201D]([^"\u201C\u201D]+)["\u201C\u201D]\s*(.*)\s*Result=true/
      );
      const searchValue = match?.[1];
      const hintForModel = match?.[2];

      if (searchValue === undefined) {
        throw new Error(`Incorrect command passed: ${cmd}`);
      }

      await scrollUntilElementVisible({
        searchConfig: { text: searchValue },
        searchValue,
        isUp,
        hintForModel,
        observer,
        usingAppetizeClone,
        appetizeCloneSession,
        session: appetizeSessionWrapper,
        handleSwipeOrScroll,
        getBase64Screenshot,
        settingsData,
      });
    },
    [
      appetizeCloneSession,
      buildData.platform,
      getBase64Screenshot,
      handleSwipeOrScroll,
      appetizeSessionWrapper,
      usingAppetizeClone,
      settingsData,
    ]
  );

  const handleTakeScreenshot = useCallback(async (cmd: string) => {
    const label = cmd.split("takeScreenshot:")[1].trim();
    console.log("Taking screenshot with label: ", label);
  }, []);

  const handleOpenInfoBar = useCallback(
    async (imageDimensions: Dimensions) => {
      const centerX = Math.floor(imageDimensions.width / 2);
      const topY = 0;

      const cmd = `slide: down 100%;${centerX};${topY};`;
      await handleSlide(cmd, imageDimensions);
    },
    [handleSlide]
  );

  const handleCloseInfoBar = useCallback(async () => {
    await handleExecuteCommand(async () => {
      const isAndroid = buildData.platform.toLowerCase() === "android";
      if (usingAppetizeClone) {
        // For appetize clone, use the back button for Android and home for iOS
        if (isAndroid) {
          console.log("Closing info bar with back button (Android)");
          return appetizeCloneSession.current.commands.keyStroke({
            keycode: 4, // BACK key
          });
        } else {
          console.log("Closing info bar with home button (iOS)");
          return appetizeCloneSession.current.commands.tapHomeScreen();
        }
      } else {
        if (isAndroid) {
          console.log("Closing info bar with back button (Android)");
          const command = "input keyevent 4"; // BACK key
          return appetizeSessionWrapper.current
            .getData()
            .adbShellCommand(command);
        } else {
          console.log("Closing info bar with home button (iOS)");
          return await Timeout.wrap(
            appetizeSessionWrapper.current.getData().keypress("HOME"),
            10000,
            "An error occurred while closing info bar"
          );
        }
      }
    }, "closeInfoBar");
  }, [
    buildData.platform,
    usingAppetizeClone,
    appetizeCloneSession,
    appetizeSessionWrapper,
  ]);

  const handlePlayAudio = useCallback(
    async (cmd: string) => {
      const regex = /play:\s*(.+)/;
      const match = cmd.match(regex);
      const filename = match ? match[1]?.trim() : null;

      return await handleExecuteCommand(
        async () => {
          if (filename) {
            const audio = companyData.audios.find(
              (audio) => audio.name === filename
            );
            const isAndroid = buildData.platform.toLowerCase() === "android";
            if (usingAppetizeClone) {
              if (isAndroid) {
                await appetizeCloneSession.current.commands.playAudio(
                  audio?.url
                );
              } else {
                return {
                  type: "error",
                  error:
                    "Play audio is not supported on this platform. Please contact Support",
                };
              }
            } else {
              return {
                type: "error",
                error:
                  "Play audio is not supported on this platform. Please contact Support",
              };
            }
          }
        },
        filename ? `playAudio: ${filename}` : "playAudio"
      );
    },
    [buildData.platform, usingAppetizeClone, appetizeCloneSession, companyData]
  );

  const handlePlayVoice = useCallback(
    async (cmd: string): Promise<CommandHandlerReturnValue | undefined> =>
      await handleExecuteCommand(
        async () => {
          const regex = /playVoice:\s*"?([^\"]+)"?/;
          const match = cmd.match(regex);
          const text = match ? match[1] : null;

          if (text) {
            if (usingAppetizeClone) {
              const voiceCommandUrl =
                await appetizeCloneSession.current.commands.playVoice(text);

              return {
                type: "success",
                data: {
                  voiceCommandUrl,
                },
              };
            } else {
              return {
                type: "error",
                error:
                  "Play voice is not supported on this platform. Please contact Support",
              };
            }
          }
        },
        (() => {
          const m = cmd.match(/playVoice:\s*"?([^\"]+)"?/);
          return m && m[1] ? `playVoice: ${m[1]}` : "playVoice";
        })()
      ),
    [usingAppetizeClone, appetizeCloneSession]
  );

  const handleListen = useCallback(
    async (): Promise<CommandHandlerReturnValue | undefined> =>
      await handleExecuteCommand(async () => {
        if (!usingAppetizeClone || !appetizeCloneSession?.current) {
          return undefined;
        }
        const response = await appetizeCloneSession.current.commands.listen();

        const lastStepNumber = getLatestStepNumber();
        gptStepsRef.current[gptStepsRef.current.length - 1] = [
          ...gptStepsRef.current.at(-1),
          `[Step ${lastStepNumber!}] remember: ${response.text}`,
        ];

        return {
          type: "success",
          data: {
            listenedAudioUrl: response.audioUrl,
          },
        };
      }, "listen"),
    [usingAppetizeClone, appetizeCloneSession, gptStepsRef]
  );

  const handleTapCenter = useCallback(async () => {
    await handleExecuteCommand(async () => {
      console.log("Tapping on center");
      if (usingAppetizeClone) {
        const { width, height } = appetizeCloneSession!.current!.device.screen;
        const x = Math.floor(width / 2);
        const y = Math.floor(height / 2);
        await appetizeCloneSession!.current!.commands.tap({
          coordinates: { x, y },
          duration: 200,
        });
      } else {
        const device = appetizeSessionWrapper?.current?.getData()?.device;
        if (device?.screen) {
          const { width, height } = device.screen;
          const x = Math.floor(width / 2);
          const y = Math.floor(height / 2);
          await appetizeSessionWrapper!.current.getData().tap({
            coordinates: { x, y },
            duration: 200,
          });
        } else {
          console.error("Device screen dimensions not available");
        }
      }
    }, "tapOn: center");
  }, [usingAppetizeClone, appetizeCloneSession, appetizeSessionWrapper]);

  const commandHandler = useCallback(
    async ({
      cmd,
      imageDimensions,
      uiElements,
      observer,
    }: {
      cmd: string;
      imageDimensions: Dimensions;
      uiElements: any[];
      observer: Subscriber<unknown>;
    }) => {
      cmd = cmd.trim();

      const normalizeCommandPrefix = (cmd: string): string => {
        // Match any command that has .id or .text (case-insensitive) followed by a colon
        return cmd.replace(
          /^([a-zA-Z]+)(\.(id|text)):/i,
          (match, commandName, dotSuffix, suffix) => {
            return `${commandName}${dotSuffix.toLowerCase()}:`;
          }
        );
      };
      // Normalize the command once at entry point
      cmd = normalizeCommandPrefix(cmd);

      console.log("normalizeCommandPrefix cmd", cmd);
      return await withPerfLog(
        "CommandHandler",
        async () => {
          if (cmd === "tapOn: center" || cmd === "tabOn: center") {
            await handleTapCenter();
          } else if (cmd.startsWith("tabOn:") || cmd.startsWith("tabOn.id:")) {
            return await handleTabOn(cmd, imageDimensions);
          } else if (cmd.startsWith("set location:")) {
            await handleSetLocation(cmd);
          } else if (cmd.startsWith("tap in sequence:")) {
            await handleTapInSequence(cmd, imageDimensions);
          } else if (cmd.startsWith("double tap:")) {
            await handleDoubleTap(cmd, imageDimensions);
          } else if (
            cmd.startsWith("multiTap.id:") ||
            cmd.startsWith("multiTap.text:")
          ) {
            await handleMultiTapOnElement(cmd, imageDimensions);
          } else if (
            cmd.startsWith("long press:") ||
            cmd.startsWith("longPressOn:") ||
            cmd.startsWith("longPressOn.id:")
          ) {
            await handleTabOn(cmd, imageDimensions, 2000);
          } else if (cmd.startsWith("wait:")) {
            await handleWait(cmd);
          } else if (cmd.startsWith("zoom:")) {
            await handleZoom(cmd);
          } else if (cmd.startsWith("type:")) {
            await handleType(cmd);
          } else if (cmd.startsWith("press:")) {
            await handleKeypress(cmd);
          } else if (cmd.startsWith("scroll:")) {
            await handleSwipeOrScroll(cmd);
          } else if (cmd.startsWith("removeText:")) {
            await handleRemoveText(cmd);
          } else if (cmd.startsWith("openAppClipLink:")) {
            await handleOpenAppClipLink(cmd);
          } else if (
            cmd.startsWith("open deeplink:") ||
            cmd.startsWith("openLink:")
          ) {
            await handleOpenDeepLink(cmd);
          } else if (cmd.startsWith("verify email:")) {
            await handleVerifyEmail(cmd);
          } else if (cmd.startsWith("swipe")) {
            await handleSwipeOrScroll(cmd);
          } else if (cmd.startsWith("slide")) {
            await handleSlide(cmd, imageDimensions);
          } else if (cmd.startsWith("execute:")) {
            await handleExecute(cmd);
          } else if (cmd.startsWith("check: audio")) {
            await handleCheckAudio();
          } else if (cmd.startsWith("go to:")) {
            await handleGoTo(cmd);
          } else if (cmd.startsWith("shake the device")) {
            await handleShakeDevice();
          } else if (cmd.startsWith("restart")) {
            await handleRestartApp();
          } else if (cmd.startsWith("terminateApp")) {
            await handleTerminateApp();
          } else if (cmd.startsWith("launchApp:")) {
            await handleLaunchApp(cmd);
          } else if (cmd.startsWith("matchBiometry:")) {
            await handleBiometry(cmd);
          } else if (cmd.startsWith("offlineMode:")) {
            return await handleGoingOfflineAndOnline(cmd);
          } else if (cmd.startsWith("curlRequest:")) {
            return await handleCurlRequest(cmd);
          } else if (cmd.startsWith("pressBackButton")) {
            await handleBackAndroid();
          } else if (
            cmd.startsWith("assertVisible.id:") ||
            cmd.startsWith("assertVisible.text:")
          ) {
            return await handleAssertVisible(cmd, uiElements);
          } else if (
            cmd.startsWith("assertNotVisible.id:") ||
            cmd.startsWith("assertNotVisible.text:")
          ) {
            return await handleAssertNotVisible(cmd, uiElements);
          } else if (
            cmd.startsWith("scrollUpUntilVisible.text:") ||
            cmd.startsWith("scrollDownUntilVisible.text:")
          ) {
            await handleScrollUntilTextVisible(cmd, uiElements, observer);
          } else if (
            cmd.startsWith("scrollUpUntilVisible.id:") ||
            cmd.startsWith("scrollDownUntilVisible.id:")
          ) {
            await handleScrollUntilIdVisible(cmd, uiElements, observer);
          } else if (cmd.startsWith("scrollToBottom")) {
            await handleScrollToBottom(cmd, observer);
          } else if (cmd.startsWith("takeScreenshot:")) {
            await handleTakeScreenshot(cmd);
          } else if (cmd.startsWith("openInfoBar")) {
            await handleOpenInfoBar(imageDimensions);
          } else if (cmd.startsWith("closeInfoBar")) {
            await handleCloseInfoBar();
          } else if (cmd.startsWith("play:")) {
            return await handlePlayAudio(cmd);
          } else if (cmd.startsWith("playVoice:")) {
            return await handlePlayVoice(cmd);
          } else if (cmd.startsWith("listen")) {
            return await handleListen();
          } else if (cmd.startsWith("extractLinkFromEmail:")) {
            await handleExtractLinkFromEmail(cmd);
          }
        },
        { command: cmd } // Truncate command for logging
      );
    },
    [
      handleTabOn,
      handleMultiTapOnElement,
      handleTapInSequence,
      handleSetLocation,
      handleDoubleTap,
      handleWait,
      handleZoom,
      handleType,
      handleKeypress,
      handleSwipeOrScroll,
      handleRemoveText,
      handleOpenDeepLink,
      handleVerifyEmail,
      handleSlide,
      handleExecute,
      handleCheckAudio,
      handleGoTo,
      handleShakeDevice,
      handleRestartApp,
      handleTerminateApp,
      handleLaunchApp,
      handleBiometry,
      handleGoingOfflineAndOnline,
      handleCurlRequest,
      handleBackAndroid,
      handleAssertVisible,
      handleAssertNotVisible,
      handleScrollUntilTextVisible,
      handleScrollUntilIdVisible,
      handleScrollToBottom,
      handleTakeScreenshot,
      handleOpenInfoBar,
      handleCloseInfoBar,
      handlePlayAudio,
      handlePlayVoice,
      handleListen,
      handleExtractLinkFromEmail,
      handleTapCenter,
    ]
  );

  return commandHandler;
};
export default useCommandHandler;
